import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import SupportMailLink from "@/components/SupportMailLink";
import AuthPromptModal from "@/components/AuthPromptModal";
import { useAnonPrompt } from "@/lib/anonPrompt";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  CheckCircle,
  Circle,
  Plus,
  X,
  Cloud,
  Info,
  CaretDown,
} from "@phosphor-icons/react";

// ---------- shared: autosave hook ----------
function useAutoSaveCheckinState(state, ready) {
  const [status, setStatus] = useState("idle"); // 'idle' | 'saving' | 'saved'
  const timerRef = useRef(null);
  useEffect(() => {
    if (!ready) return;
    setStatus("saving");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        await api.put("/checkin/state", state);
        setStatus("saved");
        setTimeout(() => setStatus(s => s === "saved" ? "idle" : s), 1500);
      } catch {
        setStatus("idle");
      }
    }, 500);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [state, ready]);
  return status;
}

function SaveIndicator({ status, t }) {
  if (status === "saving") return <span data-testid="save-indicator" className="text-xs text-ink-soft/70 font-sans">{t.checkin.savingIndicator}</span>;
  if (status === "saved")  return <span data-testid="save-indicator" className="text-xs text-sage font-sans font-semibold">✓ {t.checkin.savedIndicator}</span>;
  return null;
}

function CheckinBody({ t, content, state, setState, saveStatus }) {
  // Accordion: only one section open at a time (null = all collapsed by default)
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId(prev => prev === id ? null : id);

  const cards = [
    {
      id: "budget",
      title: t.checkin.toolBudgetTitle,
      desc: t.checkin.toolBudgetDesc,
      render: () => <BudgetTool t={t} content={content} state={state} setState={setState} />,
    },
    {
      id: "packing",
      title: t.checkin.toolPackingTitle,
      desc: t.checkin.toolPackingDesc,
      render: () => <PackingTool t={t} content={content} state={state} setState={setState} />,
    },
    {
      id: "departure",
      title: t.checkin.toolDeparturePartTitle,
      desc: t.checkin.toolDeparturePartDesc,
      render: () => <DepartureDocsTool t={t} content={content} state={state} setState={setState} />,
    },
    {
      id: "gifts",
      title: content.gifts.title,
      desc: t.checkin.toolGiftsDesc,
      render: () => <GiftsSection t={t} content={content.gifts} state={state} setState={setState} />,
    },
    {
      id: "connectivity",
      title: content.connectivity.title,
      desc: t.checkin.toolConnectivityDesc,
      render: () => <ConnectivitySection content={content.connectivity} />,
    },
  ];

  return (
    <>
      {/* Global autosave indicator */}
      <div className="flex justify-end -mt-6 h-4"><SaveIndicator status={saveStatus} t={t} /></div>

      <div className="space-y-3">
        {cards.map(c => (
          <CollapsibleCard
            key={c.id}
            id={c.id}
            title={c.title}
            desc={c.desc}
            isOpen={openId === c.id}
            onToggle={() => toggle(c.id)}
          >
            {c.render()}
          </CollapsibleCard>
        ))}
      </div>
    </>
  );
}

// ---------- Shared: collapsible card (accordion item) ----------
function CollapsibleCard({ id, title, desc, isOpen, onToggle, children }) {
  return (
    <section
      data-testid={`checkin-card-${id}`}
      data-open={isOpen ? "true" : "false"}
      className={`rounded-3xl border bg-surface-card overflow-hidden transition-colors ${
        isOpen ? "border-terracotta/40" : "border-border hover:border-terracotta/30"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`checkin-card-body-${id}`}
        data-testid={`checkin-card-toggle-${id}`}
        className="w-full text-left p-5 sm:p-6 flex items-start gap-4"
      >
        <div className="h-6 w-1 rounded-full bg-terracotta shrink-0 mt-1" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-extrabold text-xl sm:text-2xl text-ink tracking-tight leading-tight">
            {title}
          </h2>
          {desc && (
            <p className="text-sm text-ink-soft font-sans mt-1 leading-relaxed">{desc}</p>
          )}
        </div>
        <CaretDown
          size={20}
          weight="bold"
          className={`text-terracotta shrink-0 mt-1 transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          aria-hidden="true"
        />
      </button>
      <div
        id={`checkin-card-body-${id}`}
        data-testid={`checkin-card-body-${id}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 sm:px-6 pb-6 pt-1 space-y-6">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}


// ---------- MAIN: Check-in hub ----------
export default function Checkin() {
  const { t, lang, user, loadingUser } = useApp();
  const [content, setContent] = useState(null);
  const [state, setState] = useState(null);
  const [ready, setReady] = useState(false);
  const isAnonymous = !user;
  const { open: authPromptOpen, setOpen: setAuthPromptOpen, nudge: openAuthPrompt } = useAnonPrompt();

  // Fire Plausible custom event on mount so the conversion funnel can track
  // Check-in phase engagement, same pattern as Landing/Organizza/ABordo Viewed.
  // Goal must be configured on the Plausible dashboard side.
  useEffect(() => {
    trackEvent("Checkin Viewed");
  }, []);

  useEffect(() => {
    if (loadingUser) return;
    (async () => {
      try {
        // /checkin/state is now actor-aware (Fase 2): anonymous users get
        // their own draft from anon_checkin_state, registered users get the
        // canonical user_checkin_state. Same shape either way.
        const [c, s] = await Promise.all([
          api.get(`/checkin/content?lang=${lang}`),
          api.get("/checkin/state"),
        ]);
        setContent(c.data);
        setState(s.data);
        setReady(true);
      } catch { /* fail-soft */ }
    })();
  }, [user, loadingUser, lang]);

  const saveStatus = useAutoSaveCheckinState(state, ready);

  // guardedSetState: for anonymous users we still perform the mutation (it
  // saves to the anon_checkin_state shadow collection and will be migrated on
  // signup), and we fire a one-shot informational nudge modal so the user
  // knows they can move their draft to a real account.
  const guardedSetState = useCallback((updater) => {
    if (isAnonymous) openAuthPrompt(t.common.authReasonSaveCheckin);
    setState(updater);
  }, [isAnonymous, openAuthPrompt, t]);

  if (loadingUser) return <div className="p-8 text-ink-soft">{t.common.loading}</div>;
  if (!ready || !content || !state) return <div className="p-8 text-ink-soft">{t.common.loading}</div>;

  return (
    <div className="max-w-4xl mx-auto px-5 py-8 space-y-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
            {t.phase.checkin}
          </p>
          <h1 data-testid="checkin-title" className="font-display font-black text-3xl sm:text-4xl text-ink mt-1 tracking-tight">
            {t.checkin.title}
          </h1>
          <p className="text-ink-soft mt-2 leading-relaxed max-w-xl">
            {t.checkin.subtitle}
          </p>
          <p
            data-testid="checkin-timing-disclaimer"
            className="text-xs sm:text-sm text-ink-soft/80 mt-4 leading-relaxed max-w-2xl font-sans"
          >
            {t.checkin.timingDisclaimerShort}
            {" "}
            <SupportMailLink testid="checkin-support-link" subject="Segnalazione — Check-in" />
          </p>
        </div>
        <MascotPlaceholder emotion="happy" src="/assets/boardy-energico.png" />
      </div>

      <CheckinBody t={t} content={content} state={state} setState={guardedSetState} saveStatus={saveStatus} />

      <AuthPromptModal
        open={authPromptOpen}
        onOpenChange={setAuthPromptOpen}
        reason={t.common.authReasonSaveCheckin}
        afterPath="/checkin"
      />
    </div>
  );
}


// ---------- Tool 1: PACKING ----------
function PackingTool({ t, content, state, setState }) {
  const zone = state.packing?.climate_zone || null;
  const items = state.packing?.items || {};
  const customItems = state.packing?.custom_items || [];

  const setZone = (z) => setState(s => ({
    ...s,
    packing: {
      ...s.packing,
      climate_zone: z,
      items: s.packing?.items || {},
      custom_items: s.packing?.custom_items || [],
    },
  }));
  const toggleItem = (id) => setState(s => ({
    ...s,
    packing: {
      climate_zone: s.packing?.climate_zone || null,
      items: { ...(s.packing?.items || {}), [id]: !(s.packing?.items || {})[id] },
      custom_items: s.packing?.custom_items || [],
    },
  }));
  const addCustom = (sectionId, label) => {
    const clean = (label || "").trim();
    if (!clean) return;
    const id = `pkc_${sectionId}_${Date.now()}`;
    setState(s => ({
      ...s,
      packing: {
        climate_zone: s.packing?.climate_zone || null,
        items: s.packing?.items || {},
        custom_items: [...(s.packing?.custom_items || []), { id, section_id: sectionId, label: clean }],
      },
    }));
  };
  const removeCustom = (id) => setState(s => {
    const nextItems = { ...(s.packing?.items || {}) };
    delete nextItems[id];
    return {
      ...s,
      packing: {
        climate_zone: s.packing?.climate_zone || null,
        items: nextItems,
        custom_items: (s.packing?.custom_items || []).filter(ci => ci.id !== id),
      },
    };
  });

  return (
    <section data-testid="packing-tool" className="space-y-6">
      {!zone && (
        <div data-testid="packing-zone-picker" className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Cloud size={20} weight="duotone" className="text-terracotta" />
            <h3 className="font-display font-bold text-lg text-ink">{t.checkin.climateZoneQuestion}</h3>
          </div>
          <p className="text-sm text-ink-soft font-sans leading-relaxed">{t.checkin.climateZoneHint}</p>
          <RadioGroup
            value=""
            onValueChange={setZone}
            className="grid gap-2 sm:grid-cols-2"
          >
            {content.climate_zones.map(z => (
              <label
                key={z.id}
                data-testid={`packing-zone-option-${z.id}`}
                htmlFor={`zone-${z.id}`}
                className="flex items-start gap-3 rounded-2xl border border-border hover:border-terracotta/60 p-4 cursor-pointer transition-colors"
              >
                <RadioGroupItem id={`zone-${z.id}`} value={z.id} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-sans font-semibold text-ink">{z.name}</div>
                  <div className="text-xs text-ink-soft mt-0.5">{z.desc}</div>
                </div>
              </label>
            ))}
          </RadioGroup>
        </div>
      )}

      {zone && (
        <>
          <div className="rounded-2xl border border-terracotta/30 bg-terracotta/5 p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-ink font-sans">
              <Cloud size={16} weight="duotone" className="text-terracotta" />
              <span className="font-semibold">
                {(content.climate_zones.find(z => z.id === zone) || {}).name}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setZone(null)}
              data-testid="packing-change-zone"
              className="text-terracotta hover:bg-terracotta/10 rounded-full"
            >
              {t.checkin.climateChangeCta}
            </Button>
          </div>

          <div data-testid="packing-intro-note" className="rounded-2xl bg-sand/25 border border-sand/50 p-4 text-sm text-ink font-sans leading-relaxed italic">
            {content.packing.intro_note}
          </div>

          <div className="space-y-6">
            {content.packing.sections.map(section => {
              const sectionItems = section.climate_specific
                ? getPackingItemsForZone(section, zone)
                : section.items || [];
              const sectionCustom = customItems.filter(ci => ci.section_id === section.id);
              return (
                <PackingSection
                  key={section.id}
                  t={t}
                  section={section}
                  items={sectionItems}
                  customItems={sectionCustom}
                  checkedMap={items}
                  onToggle={toggleItem}
                  onAddCustom={(label) => addCustom(section.id, label)}
                  onRemoveCustom={removeCustom}
                />
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

function getPackingItemsForZone(section, zone) {
  const byZone = section.items_by_zone || {};
  const common = byZone.common || [];
  if (zone === "unknown") {
    // merge all zones + common, dedup by id
    const seen = new Set();
    const merged = [];
    for (const z of ["northeast_midwest", "south_westcoast", "rural"]) {
      for (const it of (byZone[z] || [])) {
        if (!seen.has(it.id)) { seen.add(it.id); merged.push(it); }
      }
    }
    for (const it of common) {
      if (!seen.has(it.id)) { seen.add(it.id); merged.push(it); }
    }
    return merged;
  }
  return [...(byZone[zone] || []), ...common];
}

function PackingSection({ t, section, items, customItems, checkedMap, onToggle, onAddCustom, onRemoveCustom }) {
  const [draft, setDraft] = React.useState("");
  const totalCount = items.length + customItems.length;
  const doneCount = items.filter(i => checkedMap[i.id]).length
    + customItems.filter(i => checkedMap[i.id]).length;

  const submitDraft = () => {
    if (!draft.trim()) return;
    onAddCustom(draft);
    setDraft("");
  };
  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); submitDraft(); }
  };

  return (
    <div data-testid={`packing-section-${section.id}`} className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <h3 className="font-display font-extrabold text-lg text-ink tracking-tight">
          {section.label}
        </h3>
        <span className="text-xs text-ink-soft font-sans">{doneCount} / {totalCount}</span>
      </div>
      {section.note && (
        <p className="text-xs text-ink-soft italic mb-3">{section.note}</p>
      )}
      <ul className="space-y-1.5">
        {items.map(it => {
          const checked = !!checkedMap[it.id];
          return (
            <li key={it.id}>
              <button
                type="button"
                data-testid={`packing-toggle-${it.id}`}
                onClick={() => onToggle(it.id)}
                className="w-full flex items-start gap-3 p-2 rounded-xl hover:bg-sand/20 transition-colors text-left"
              >
                {checked
                  ? <CheckCircle size={22} weight="duotone" className="text-sage shrink-0 mt-0.5" />
                  : <Circle size={22} weight="regular" className="text-ink-soft/40 shrink-0 mt-0.5" />}
                <span className={`text-sm font-sans text-ink ${checked ? "opacity-60" : ""}`}>
                  {it.label}
                </span>
              </button>
            </li>
          );
        })}
        {customItems.map(ci => {
          const checked = !!checkedMap[ci.id];
          return (
            <li key={ci.id} data-testid={`packing-custom-row-${ci.id}`} className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`packing-toggle-${ci.id}`}
                onClick={() => onToggle(ci.id)}
                className="flex-1 flex items-start gap-3 p-2 rounded-xl hover:bg-sand/20 transition-colors text-left"
              >
                {checked
                  ? <CheckCircle size={22} weight="duotone" className="text-sage shrink-0 mt-0.5" />
                  : <Circle size={22} weight="regular" className="text-ink-soft/40 shrink-0 mt-0.5" />}
                <span className={`text-sm font-sans text-ink ${checked ? "opacity-60" : ""} flex items-center gap-2 flex-wrap`}>
                  {ci.label}
                  <span className="text-[9px] uppercase tracking-widest font-sans font-bold text-terracotta bg-terracotta/10 px-1.5 py-0.5 rounded-full">
                    {t.checkin.customBadge}
                  </span>
                </span>
              </button>
              <button
                type="button"
                data-testid={`packing-remove-${ci.id}`}
                onClick={() => onRemoveCustom(ci.id)}
                aria-label={t.checkin.removeRow}
                className="w-8 h-8 rounded-lg text-ink-soft hover:text-terracotta hover:bg-terracotta/10 flex items-center justify-center shrink-0"
              >
                <X size={14} weight="bold" />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-2">
        <Input
          data-testid={`packing-custom-input-${section.id}`}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder={t.checkin.customLabelPh}
          className="flex-1 h-9 text-sm rounded-lg"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={submitDraft}
          disabled={!draft.trim()}
          data-testid={`packing-add-custom-${section.id}`}
          className="text-terracotta hover:bg-terracotta/10 rounded-full disabled:opacity-40"
        >
          <Plus size={14} weight="bold" className="mr-1" />
          {t.checkin.addRow}
        </Button>
      </div>
    </div>
  );
}

// ---------- Tool 2: BUDGET ----------
function BudgetTool({ t, content, state, setState }) {
  return (
    <section data-testid="budget-tool" className="space-y-6">
      <div data-testid="budget-intro-note" className="rounded-2xl bg-sand/25 border border-sand/50 p-4 text-sm text-ink font-sans leading-relaxed italic">
        {content.budget.intro_note}
      </div>

      <BudgetTable
        t={t}
        tableKey="fixed"
        title={content.budget.fixed.title}
        subtitle={content.budget.fixed.subtitle}
        seedItems={content.budget.fixed.items}
        rows={state.budget?.fixed || []}
        onChange={(rows) => setState(s => ({ ...s, budget: { ...s.budget, fixed: rows } }))}
      />
    </section>
  );
}

function BudgetTable({ t, tableKey, title, subtitle, seedItems, rows, onChange }) {
  // Merge seed items with saved rows: preserve saved amount by id; unknown saved rows are custom.
  const byId = Object.fromEntries(rows.map(r => [r.id, r]));
  const seedRows = seedItems.map(si => ({
    id: si.id,
    label: si.label,
    amount: byId[si.id]?.amount ?? null,
    custom: false,
  }));
  const customRows = rows.filter(r => r.custom);

  const total = [...seedRows, ...customRows]
    .map(r => Number(r.amount))
    .filter(v => !Number.isNaN(v))
    .reduce((a, b) => a + b, 0);

  const updateAmount = (id, val) => {
    const clean = val === "" ? null : Number(val);
    const next = [...rows.filter(r => r.id !== id), { id, amount: Number.isNaN(clean) ? null : clean, custom: byId[id]?.custom || false, label: byId[id]?.label }];
    // preserve custom label
    onChange(next);
  };
  const updateLabel = (id, label) => {
    const next = rows.map(r => r.id === id ? { ...r, label } : r);
    onChange(next);
  };
  const addCustom = () => {
    const newId = `custom_${tableKey}_${Date.now()}`;
    onChange([...rows, { id: newId, label: "", amount: null, custom: true }]);
  };
  const removeCustom = (id) => {
    onChange(rows.filter(r => r.id !== id));
  };

  return (
    <div data-testid={`budget-table-${tableKey}`} className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6">
      <h3 className="font-display font-extrabold text-xl text-ink tracking-tight">{title}</h3>
      <p className="text-xs text-ink-soft font-sans uppercase tracking-widest mt-1">{subtitle}</p>

      <ul className="mt-4 space-y-1.5">
        {seedRows.map(r => (
          <li key={r.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-sand/10">
            <span className="flex-1 text-sm text-ink font-sans">{r.label}</span>
            <Input
              data-testid={`budget-amount-${r.id}`}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder={t.checkin.amountPh}
              value={r.amount ?? ""}
              onChange={(e) => updateAmount(r.id, e.target.value)}
              className="w-32 h-9 text-sm text-right rounded-lg"
            />
          </li>
        ))}
        {customRows.map(r => (
          <li key={r.id} data-testid={`budget-custom-row-${r.id}`} className="flex items-center gap-2 p-2 rounded-xl bg-sand/10">
            <Input
              data-testid={`budget-custom-label-${r.id}`}
              type="text"
              placeholder={t.checkin.customLabelPh}
              value={r.label || ""}
              onChange={(e) => updateLabel(r.id, e.target.value)}
              className="flex-1 h-9 text-sm rounded-lg"
            />
            <Input
              data-testid={`budget-amount-${r.id}`}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder={t.checkin.amountPh}
              value={r.amount ?? ""}
              onChange={(e) => updateAmount(r.id, e.target.value)}
              className="w-32 h-9 text-sm text-right rounded-lg"
            />
            <button
              type="button"
              data-testid={`budget-remove-${r.id}`}
              onClick={() => removeCustom(r.id)}
              aria-label={t.checkin.removeRow}
              className="w-8 h-8 rounded-lg text-ink-soft hover:text-terracotta hover:bg-terracotta/10 flex items-center justify-center shrink-0"
            >
              <X size={14} weight="bold" />
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center justify-between gap-3 pt-4 border-t border-border/60">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addCustom}
          data-testid={`budget-add-${tableKey}`}
          className="text-terracotta hover:bg-terracotta/10 rounded-full"
        >
          <Plus size={14} weight="bold" className="mr-1" />
          {t.checkin.addRow}
        </Button>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-ink-soft font-sans font-bold">
            {t.checkin.total}
          </div>
          <div
            data-testid={`budget-total-${tableKey}`}
            className="font-display font-extrabold text-2xl text-ink"
          >
            € {total.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Tool 3: DEPARTURE DOCS ----------
function DepartureDocsTool({ t, content, state, setState }) {
  const items = state.departure?.items || {};
  const customItems = state.departure?.custom_items || [];
  const [draft, setDraft] = React.useState("");

  const toggle = (id) => setState(s => ({
    ...s,
    departure: {
      items: { ...(s.departure?.items || {}), [id]: !(s.departure?.items || {})[id] },
      custom_items: s.departure?.custom_items || [],
    },
  }));
  const addCustom = () => {
    const clean = draft.trim();
    if (!clean) return;
    const id = `depc_${Date.now()}`;
    setState(s => ({
      ...s,
      departure: {
        items: s.departure?.items || {},
        custom_items: [...(s.departure?.custom_items || []), { id, label: clean }],
      },
    }));
    setDraft("");
  };
  const removeCustom = (id) => setState(s => {
    const nextItems = { ...(s.departure?.items || {}) };
    delete nextItems[id];
    return {
      ...s,
      departure: {
        items: nextItems,
        custom_items: (s.departure?.custom_items || []).filter(ci => ci.id !== id),
      },
    };
  });
  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); addCustom(); }
  };

  const done = content.departure_docs.filter(d => items[d.id]).length
    + customItems.filter(ci => items[ci.id]).length;
  const total = content.departure_docs.length + customItems.length;

  return (
    <section data-testid="departure-tool" className="space-y-6">

      <div className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-display font-extrabold text-lg text-ink tracking-tight">
            {t.checkin.toolDeparturePartTitle}
          </h3>
          <span
            data-testid="departure-counter"
            className="text-sm text-ink-soft font-sans"
          >
            {t.checkin.documentsCounter(done, total)}
          </span>
        </div>
        <ul className="space-y-1.5">
          {content.departure_docs.map(d => {
            const checked = !!items[d.id];
            return (
              <li key={d.id}>
                <button
                  type="button"
                  data-testid={`departure-toggle-${d.id}`}
                  onClick={() => toggle(d.id)}
                  className="w-full flex items-start gap-3 p-2 rounded-xl hover:bg-sand/20 transition-colors text-left"
                >
                  {checked
                    ? <CheckCircle size={24} weight="duotone" className="text-sage shrink-0 mt-0.5" />
                    : <Circle size={24} weight="regular" className="text-ink-soft/40 shrink-0 mt-0.5" />}
                  <span className={`text-sm font-sans text-ink ${checked ? "opacity-60" : ""}`}>
                    {d.label}
                  </span>
                </button>
              </li>
            );
          })}
          {customItems.map(ci => {
            const checked = !!items[ci.id];
            return (
              <li key={ci.id} data-testid={`departure-custom-row-${ci.id}`} className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`departure-toggle-${ci.id}`}
                  onClick={() => toggle(ci.id)}
                  className="flex-1 flex items-start gap-3 p-2 rounded-xl hover:bg-sand/20 transition-colors text-left"
                >
                  {checked
                    ? <CheckCircle size={24} weight="duotone" className="text-sage shrink-0 mt-0.5" />
                    : <Circle size={24} weight="regular" className="text-ink-soft/40 shrink-0 mt-0.5" />}
                  <span className={`text-sm font-sans text-ink ${checked ? "opacity-60" : ""} flex items-center gap-2 flex-wrap`}>
                    {ci.label}
                    <span className="text-[9px] uppercase tracking-widest font-sans font-bold text-terracotta bg-terracotta/10 px-1.5 py-0.5 rounded-full">
                      {t.checkin.customBadge}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  data-testid={`departure-remove-${ci.id}`}
                  onClick={() => removeCustom(ci.id)}
                  aria-label={t.checkin.removeRow}
                  className="w-8 h-8 rounded-lg text-ink-soft hover:text-terracotta hover:bg-terracotta/10 flex items-center justify-center shrink-0"
                >
                  <X size={14} weight="bold" />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-2">
          <Input
            data-testid="departure-custom-input"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            placeholder={t.checkin.customLabelPh}
            className="flex-1 h-9 text-sm rounded-lg"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addCustom}
            disabled={!draft.trim()}
            data-testid="departure-add-custom"
            className="text-terracotta hover:bg-terracotta/10 rounded-full disabled:opacity-40"
          >
            <Plus size={14} weight="bold" className="mr-1" />
            {t.checkin.addRow}
          </Button>
        </div>
      </div>
    </section>
  );
}

// ---------- Section 4: GIFTS (custom list + text) ----------
function GiftsSection({ t, content, state, setState }) {
  const items = state.gifts?.items || [];
  const [draft, setDraft] = React.useState("");

  const add = () => {
    const clean = draft.trim();
    if (!clean) return;
    const id = `gift_${Date.now()}`;
    setState(s => ({
      ...s,
      gifts: { items: [...(s.gifts?.items || []), { id, label: clean, checked: false }] },
    }));
    setDraft("");
  };
  const toggle = (id) => setState(s => ({
    ...s,
    gifts: {
      items: (s.gifts?.items || []).map(i => i.id === id ? { ...i, checked: !i.checked } : i),
    },
  }));
  const remove = (id) => setState(s => ({
    ...s,
    gifts: { items: (s.gifts?.items || []).filter(i => i.id !== id) },
  }));
  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); add(); }
  };

  return (
    <section data-testid="gifts-section" className="space-y-6">
      <DetailsCard t={t} details={content.details} testid="gifts" />

      <div className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6">
        <h3 className="font-display font-extrabold text-lg text-ink tracking-tight mb-3">
          {t.checkin.giftsListTitle}
        </h3>
        {items.length === 0 && (
          <p className="text-sm text-ink-soft/80 italic font-sans mb-3">
            {t.checkin.giftsListEmpty}
          </p>
        )}
        <ul className="space-y-1.5">
          {items.map(it => (
            <li key={it.id} data-testid={`gift-row-${it.id}`} className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`gift-toggle-${it.id}`}
                onClick={() => toggle(it.id)}
                className="flex-1 flex items-start gap-3 p-2 rounded-xl hover:bg-sand/20 transition-colors text-left"
              >
                {it.checked
                  ? <CheckCircle size={22} weight="duotone" className="text-sage shrink-0 mt-0.5" />
                  : <Circle size={22} weight="regular" className="text-ink-soft/40 shrink-0 mt-0.5" />}
                <span className={`text-sm font-sans text-ink ${it.checked ? "opacity-60" : ""}`}>
                  {it.label}
                </span>
              </button>
              <button
                type="button"
                data-testid={`gift-remove-${it.id}`}
                onClick={() => remove(it.id)}
                aria-label={t.checkin.removeRow}
                className="w-8 h-8 rounded-lg text-ink-soft hover:text-terracotta hover:bg-terracotta/10 flex items-center justify-center shrink-0"
              >
                <X size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-2">
          <Input
            data-testid="gifts-input"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            placeholder={content.list_placeholder}
            className="flex-1 h-9 text-sm rounded-lg"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={add}
            disabled={!draft.trim()}
            data-testid="gifts-add"
            className="text-terracotta hover:bg-terracotta/10 rounded-full disabled:opacity-40"
          >
            <Plus size={14} weight="bold" className="mr-1" />
            {t.checkin.addRow}
          </Button>
        </div>
      </div>
    </section>
  );
}


// ---------- Section 5: CONNECTIVITY (informational text only) ----------
function ConnectivitySection({ content }) {
  const { t } = useApp();
  return (
    <section data-testid="connectivity-section" className="space-y-6">
      <DetailsCard t={t} details={content.details} testid="connectivity" />
    </section>
  );
}


// ---------- Shared: why/how/warning card ----------
function DetailsCard({ t, details, testid }) {
  if (!details) return null;
  return (
    <div
      data-testid={`${testid}-details`}
      className="rounded-3xl border border-border bg-surface-card p-5 sm:p-6 space-y-4"
    >
      {details.why && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-sans font-bold text-terracotta mb-1">
            {t.checkin.labelWhy}
          </div>
          <p className="text-sm text-ink font-sans leading-relaxed">{details.why}</p>
        </div>
      )}
      {details.how && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-sans font-bold text-sage mb-1">
            {t.checkin.labelHow}
          </div>
          <p className="text-sm text-ink font-sans leading-relaxed">{details.how}</p>
        </div>
      )}
      {details.warning && (
        <div className="rounded-2xl bg-terracotta/5 border border-terracotta/20 p-3 flex items-start gap-2">
          <Info size={16} weight="duotone" className="text-terracotta shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-sans font-bold text-terracotta mb-1">
              {t.checkin.labelWarning}
            </div>
            <p className="text-sm text-ink font-sans leading-relaxed">{details.warning}</p>
          </div>
        </div>
      )}
    </div>
  );
}
